import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select } from './ui/select';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ListStartupModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Master data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [employeeCounts, setEmployeeCounts] = useState([]);
  
  // Filtered data
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);

  const [formData, setFormData] = useState({
    startup_name: '',
    logo: null,
    founding_year: new Date().getFullYear(),
    industry: '',
    country_id: '',
    state_id: '',
    city: '',
    employee_count: '',
    website_url: '',
    contact_email: '',
    contact_phone: '',
    contact_country_code: '+91',
    founders: [{ name: '', phone: '', email: '', country_code: '+91', designation: '' }]
  });

  useEffect(() => {
    if (open) {
      fetchMasterData();
    }
  }, [open]);

  useEffect(() => {
    if (formData.country_id) {
      setFilteredStates(states.filter(s => s.country_id === formData.country_id));
    } else {
      setFilteredStates([]);
    }
    setFormData(prev => ({ ...prev, state_id: '', city: '' }));
  }, [formData.country_id, states]);

  useEffect(() => {
    if (formData.state_id) {
      setFilteredCities(cities.filter(c => c.state_id === formData.state_id));
    } else {
      setFilteredCities([]);
    }
    setFormData(prev => ({ ...prev, city: '' }));
  }, [formData.state_id, cities]);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const [countriesRes, statesRes, citiesRes, industriesRes, employeeCountsRes] = await Promise.all([
        axios.get(`${API}/master/countries`),
        axios.get(`${API}/master/states`),
        axios.get(`${API}/master/cities`),
        axios.get(`${API}/master/industries`),
        axios.get(`${API}/master/employee-counts`)
      ]);

      setCountries(countriesRes.data.data || []);
      setStates(statesRes.data.data || []);
      setCities(citiesRes.data.data || []);
      setIndustries(industriesRes.data.data || []);
      setEmployeeCounts(employeeCountsRes.data.data || []);
    } catch (err) {
      console.error('Error fetching master data:', err);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFounderChange = (index, field, value) => {
    const newFounders = [...formData.founders];
    newFounders[index][field] = value;
    setFormData(prev => ({ ...prev, founders: newFounders }));
  };

  const addFounder = () => {
    setFormData(prev => ({
      ...prev,
      founders: [...prev.founders, { name: '', phone: '', email: '', country_code: '+91', designation: '' }]
    }));
  };

  const removeFounder = (index) => {
    if (formData.founders.length > 1) {
      setFormData(prev => ({
        ...prev,
        founders: prev.founders.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.startup_name || !formData.industry || !formData.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/startups/?is_public=true`, formData);
      toast.success('Startup submitted successfully! We will review and contact you soon.');
      onOpenChange(false);
      
      // Reset form
      setFormData({
        startup_name: '',
        logo: null,
        founding_year: new Date().getFullYear(),
        industry: '',
        country_id: '',
        state_id: '',
        city: '',
        employee_count: '',
        website_url: '',
        contact_email: '',
        contact_phone: '',
        contact_country_code: '+91',
        founders: [{ name: '', phone: '', email: '', country_code: '+91', designation: '' }]
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit startup');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">List Your Startup</DialogTitle>
          <DialogDescription>
            Submit your startup details to be featured on Starticals
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Startup Name */}
            <div className="space-y-2">
              <Label htmlFor="startup_name">Startup Name *</Label>
              <Input
                id="startup_name"
                name="startup_name"
                value={formData.startup_name}
                onChange={handleChange}
                placeholder="Enter your startup name"
                required
              />
            </div>

            {/* Logo */}
            <div className="space-y-2">
              <Label htmlFor="logo">Logo</Label>
              <Input
                id="logo"
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
              />
              {formData.logo && (
                <img src={formData.logo} alt="Logo preview" className="h-20 w-20 object-contain mt-2" />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Founding Year */}
              <div className="space-y-2">
                <Label htmlFor="founding_year">Founding Year *</Label>
                <Input
                  id="founding_year"
                  name="founding_year"
                  type="number"
                  min="1900"
                  max={new Date().getFullYear()}
                  value={formData.founding_year}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Industry */}
              <div className="space-y-2">
                <Label htmlFor="industry">Industry *</Label>
                <Select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Industry</option>
                  {industries.map(industry => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Employee Count */}
              <div className="space-y-2">
                <Label htmlFor="employee_count">Employee Count</Label>
                <Select
                  id="employee_count"
                  name="employee_count"
                  value={formData.employee_count}
                  onChange={handleChange}
                >
                  <option value="">Select Range</option>
                  {employeeCounts.map(count => (
                    <option key={count.id} value={count.id}>
                      {count.range}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Website */}
              <div className="space-y-2">
                <Label htmlFor="website_url">Website URL</Label>
                <Input
                  id="website_url"
                  name="website_url"
                  type="url"
                  value={formData.website_url}
                  onChange={handleChange}
                  placeholder="https://yourcompany.com"
                />
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country_id">Country</Label>
                <Select
                  id="country_id"
                  name="country_id"
                  value={formData.country_id}
                  onChange={handleChange}
                >
                  <option value="">Select Country</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="state_id">State</Label>
                <Select
                  id="state_id"
                  name="state_id"
                  value={formData.state_id}
                  onChange={handleChange}
                  disabled={!formData.country_id}
                >
                  <option value="">Select State</option>
                  {filteredStates.map(state => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Select
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={!formData.state_id}
                >
                  <option value="">Select City</option>
                  {filteredCities.map(city => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4 p-4 bg-accent/50 rounded-lg">
              <h3 className="font-semibold">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email *</Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="contact@startup.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <div className="flex gap-2">
                    <Select
                      name="contact_country_code"
                      value={formData.contact_country_code}
                      onChange={handleChange}
                      className="w-24"
                    >
                      <option value="+91">+91</option>
                      <option value="+1">+1</option>
                      <option value="+44">+44</option>
                    </Select>
                    <Input
                      id="contact_phone"
                      name="contact_phone"
                      type="tel"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      placeholder="Phone number"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Founders */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-lg">Founders</Label>
                <Button type="button" size="sm" onClick={addFounder} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Founder
                </Button>
              </div>

              {formData.founders.map((founder, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">Founder {index + 1}</h4>
                    {formData.founders.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFounder(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      placeholder="Name"
                      value={founder.name}
                      onChange={(e) => handleFounderChange(index, 'name', e.target.value)}
                    />
                    <Input
                      placeholder="Designation"
                      value={founder.designation}
                      onChange={(e) => handleFounderChange(index, 'designation', e.target.value)}
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={founder.email}
                      onChange={(e) => handleFounderChange(index, 'email', e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Select
                        value={founder.country_code}
                        onChange={(e) => handleFounderChange(index, 'country_code', e.target.value)}
                        className="w-24"
                      >
                        <option value="+91">+91</option>
                        <option value="+1">+1</option>
                        <option value="+44">+44</option>
                      </Select>
                      <Input
                        type="tel"
                        placeholder="Phone"
                        value={founder.phone}
                        onChange={(e) => handleFounderChange(index, 'phone', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <>Submit Startup</>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
